import { useCallback, useEffect, useRef, useState } from "react";
import {
  aiDraftEventsUrl,
  getAiDraftGenerations,
  getAiDraftModels,
  markAiDraftViewed,
  requestAiDraft,
  selectAiDraft,
} from "../../../../service/AiDraftService";
import { deriveAiDraftState, generationLabels, isPending, isReady, runningCount } from "./aiDraftState";

export const POLL_INTERVAL_MS = 5000;
export const POLL_GIVE_UP_MS = 10 * 60 * 1000;

const messageOf = (response) => response?.data?.message || null;

/**
 * AI Draft generations for the open study (one model each; every Generate adds one). Loads on
 * open, so the tabs survive reloads and other workstations, listens to the per-study SSE stream,
 * and polls every 5 s only while a generation is running and the stream is down. Everything is
 * torn down when the study closes.
 *
 * @param studyId DB study id of the open study
 * @param active  false for users who cannot use the feature; nothing is fetched then
 */
const useAiDraft = (studyId, active) => {
  const [data, setData] = useState(null);
  const [models, setModels] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const [busy, setBusy] = useState(false);
  const [sseHealthy, setSseHealthy] = useState(false);
  const statusesRef = useRef(null);
  const runningSinceRef = useRef(null);
  const studyIdRef = useRef(studyId);
  studyIdRef.current = studyId;

  const generations = Array.isArray(data?.generations) ? data.generations : [];
  const state = deriveAiDraftState(data);
  const running = runningCount(generations);

  const refresh = useCallback(async () => {
    if (!active || !studyId) return null;
    const response = await getAiDraftGenerations(studyId);
    // ignore answers for a study that is no longer open
    if (studyIdRef.current !== studyId) return null;
    if (response?.status === 200 && response.data?.data) {
      setData(response.data.data);
      return response.data.data;
    }
    if (response?.status === 403 || response?.status === 404) setData({ enabled: false });
    return null;
  }, [active, studyId]);

  // load on open, reset on study change
  useEffect(() => {
    setData(null);
    setAnnouncement("");
    statusesRef.current = null;
    runningSinceRef.current = null;
    refresh();
  }, [refresh]);

  const enabled = !!data?.enabled;

  // the models offered in the dropdown, with the user's default
  useEffect(() => {
    if (!enabled || models) return;
    getAiDraftModels().then((response) => {
      if (response?.status === 200 && response.data?.data) setModels(response.data.data);
    });
  }, [enabled, models]);

  // announce a generation finishing in this session (not one already finished on load)
  useEffect(() => {
    if (!data) return;
    const labels = generationLabels(generations);
    const previous = statusesRef.current;
    const next = {};
    generations.forEach((g) => {
      next[g.id] = g.status;
      const was = previous?.[g.id];
      if (!was || !isPending({ status: was }) || isPending(g)) return;
      setAnnouncement(isReady(g) ? `AI draft ready: ${labels[g.id]}` : `AI draft failed: ${labels[g.id]}`);
    });
    statusesRef.current = next;
    runningSinceRef.current = running > 0 ? runningSinceRef.current ?? Date.now() : null;
  }, [data]);

  // push channel
  useEffect(() => {
    if (!active || !studyId || !enabled || typeof EventSource === "undefined") return undefined;
    const source = new EventSource(aiDraftEventsUrl(studyId), { withCredentials: true });
    source.onopen = () => setSseHealthy(true);
    source.onerror = () => setSseHealthy(false); // EventSource reconnects by itself
    source.addEventListener("ai_draft.updated", () => {
      setSseHealthy(true);
      refresh();
    });
    return () => {
      source.close();
      setSseHealthy(false);
    };
  }, [active, studyId, enabled, refresh]);

  // polling fallback while any generation is running without a working stream
  useEffect(() => {
    if (running === 0 || sseHealthy) return undefined;
    const timer = setInterval(() => {
      if (runningSinceRef.current && Date.now() - runningSinceRef.current > POLL_GIVE_UP_MS) {
        clearInterval(timer);
        return;
      }
      refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [running, sseHealthy, refresh]);

  /**
   * Starts a new generation with one model.
   * @returns {{outcome: "ok"|"noKeyImages"|"busy"|"error", id?: string, message?: string}}
   */
  const generate = useCallback(
    async (modelId = null, templateId = null, output = null) => {
      if (!studyId || busy) return { outcome: "error" };
      setBusy(true);
      try {
        const response = await requestAiDraft(studyId, modelId, templateId, output);
        if (response?.status === 202) {
          const created = response.data?.data || {};
          // show the new tab straight away; the refresh fills in the rest
          setData((prev) => ({
            ...(prev || { enabled: true }),
            generations: [...(prev?.generations || []), { ...created, stale: false, running: true }],
          }));
          refresh();
          return { outcome: "ok", id: created.id };
        }
        await refresh();
        if (response?.status === 409) return { outcome: "busy", message: messageOf(response) };
        if (response?.status === 422 && /key image/i.test(messageOf(response) || ""))
          return { outcome: "noKeyImages", message: messageOf(response) };
        return { outcome: "error", message: messageOf(response) };
      } finally {
        setBusy(false);
      }
    },
    [studyId, busy, refresh]
  );

  /** The radiologist opened this generation's tab. */
  const markViewed = useCallback(
    async (generationId) => {
      const generation = generations.find((g) => g.id === generationId);
      if (!isReady(generation) || generation.viewed_at) return;
      const now = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        generations: prev.generations.map((g) => (g.id === generationId ? { ...g, viewed_at: now } : g)),
      }));
      await markAiDraftViewed(studyId, generationId);
    },
    [generations, studyId]
  );

  /** This generation's draft was inserted into the report. */
  const markSelected = useCallback(
    async (generationId) => {
      if (!generationId) return;
      const now = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        generations: prev.generations.map((g) => (g.id === generationId ? { ...g, selected: true, selected_at: now } : g)),
      }));
      await selectAiDraft(studyId, generationId);
    },
    [studyId]
  );

  return {
    state,
    generations,
    // the latest generation (button tooltip)
    job: generations[generations.length - 1] || null,
    runningCount: running,
    maxConcurrent: data?.maxConcurrent || 3,
    keyImageCount: data?.keyImageCount || 0,
    models,
    busy,
    announcement,
    clearAnnouncement: () => setAnnouncement(""),
    refresh,
    generate,
    markViewed,
    markSelected,
  };
};

export default useAiDraft;
